import {ChevronDownIcon} from "lucide-react";
import {useEffect, useState} from "react";

import {Collapsible, CollapsibleContent, CollapsibleTrigger} from "@/components/ui/collapsible";
import {Label} from "@/components/ui/label";
import {cn} from "@/lib/utils";

import {
  SAFELISTING_CORRECT_HELP_HEADLINE,
  SAFELISTING_CORRECT_HELP_LINES,
  SAFELISTING_OVERRIDE_RECIPE_HEADLINE,
  SAFELISTING_OVERRIDE_RECIPE_STEPS,
} from "../lib/safelisting-override-help";

type SafelistingOverrideHelpCardProps = {
  variant: "create" | "correct";
  /** Expand recipe when permit banner is warning or destructive. */
  autoExpand?: boolean;
  /** Shown on the same row as the trigger (create sheet Overrides heading). */
  sectionTitle?: string;
};

export function SafelistingOverrideHelpCard({
  variant,
  autoExpand = false,
  sectionTitle,
}: SafelistingOverrideHelpCardProps) {
  const [open, setOpen] = useState(false);
  const triggerLabel = variant === "create" ? "Which situation?" : "How does correction work?";

  useEffect(() => {
    if (autoExpand) {
      setOpen(true);
    }
  }, [autoExpand]);

  const steps =
    variant === "create" ? SAFELISTING_OVERRIDE_RECIPE_STEPS : SAFELISTING_CORRECT_HELP_LINES;
  const headline =
    variant === "create"
      ? SAFELISTING_OVERRIDE_RECIPE_HEADLINE
      : SAFELISTING_CORRECT_HELP_HEADLINE;

  return (
    <Collapsible open={open} onOpenChange={setOpen} className="flex flex-col gap-2">
      <div
        className={cn(
          "flex items-center gap-2",
          sectionTitle ? "justify-between" : "justify-end",
        )}
      >
        {sectionTitle ? (
          <Label className="text-foreground text-sm font-medium">{sectionTitle}</Label>
        ) : null}
        <CollapsibleTrigger
          type="button"
          className="text-primary inline-flex shrink-0 items-center gap-1 text-xs font-medium underline-offset-2 hover:underline"
        >
          {triggerLabel}
          <ChevronDownIcon
            className={cn("size-3.5 transition-transform", open && "rotate-180")}
            aria-hidden
          />
        </CollapsibleTrigger>
      </div>
      <CollapsibleContent>
        <div className="border-border/60 bg-muted/40 flex flex-col gap-2 rounded-md border px-3 py-2.5">
          <p className="text-foreground text-xs leading-snug font-medium">{headline}</p>
          <ol className="text-muted-foreground flex list-decimal flex-col gap-1 pl-4 text-xs leading-snug">
            {steps.map((step) => (
              <li key={step}>{step}</li>
            ))}
          </ol>
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}

/** Switch row labels — overrides FormLabel default gray so name reads above hint. */
export const SAFELISTING_OVERRIDE_SWITCH_LABEL_CLASS =
  "text-foreground !mt-0 text-sm font-medium";

export const SAFELISTING_OVERRIDE_FIELD_HINT_CLASS =
  "text-muted-foreground text-xs leading-snug";
